import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { surveyService } from '../../services/survey.service';
import { Survey, SurveyStatus } from '../../types/survey.types';

// Phase 5 - US3: Contractor Dashboard (T097)

const ContractorDashboard = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      // Load only SCHEDULED and ACTIVE surveys for contractor
      const [scheduled, active] = await Promise.all([
        surveyService.listSurveys({ status: SurveyStatus.SCHEDULED }),
        surveyService.listSurveys({ status: SurveyStatus.ACTIVE }),
      ]);

      setSurveys([...scheduled.data, ...active.data]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: SurveyStatus) => {
    const colors: Record<SurveyStatus, string> = {
      [SurveyStatus.DRAFT]: 'bg-gray-100 text-gray-800',
      [SurveyStatus.SCHEDULED]: 'bg-blue-100 text-blue-800',
      [SurveyStatus.ACTIVE]: 'bg-green-100 text-green-800',
      [SurveyStatus.PAUSED]: 'bg-yellow-100 text-yellow-800',
      [SurveyStatus.COMPLETED]: 'bg-purple-100 text-purple-800',
      [SurveyStatus.CANCELED]: 'bg-red-100 text-red-800',
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status]}`}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Minhas Pesquisas</h1>
        <p className="mt-2 text-gray-600">
          Gerencie suas pesquisas ativas e envie convites aos participantes
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {surveys.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Nenhuma pesquisa ativa
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Não há pesquisas agendadas ou ativas no momento.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {surveys.map((survey) => (
            <Link
              key={survey.id}
              to={`/contractor/surveys/${survey.id}`}
              className="block"
            >
              <div className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6 border border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex-1">
                    {survey.name}
                  </h3>
                  {getStatusBadge(survey.status)}
                </div>

                {survey.description && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {survey.description}
                  </p>
                )}

                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center">
                    <svg
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span>
                      {new Date(survey.startDate).toLocaleDateString('pt-BR')} -{' '}
                      {new Date(survey.endDate).toLocaleDateString('pt-BR')}
                    </span>
                  </div>

                  <div className="flex items-center">
                    <svg
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>{survey.geographicScope}</span>
                  </div>

                  {survey.surveyValue && (
                    <div className="flex items-center">
                      <svg
                        className="h-4 w-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>
                        R$ {survey.surveyValue.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractorDashboard;
